import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { formatWeekdayName } from '../../utils/dateFormatter';
import { money, percent, count } from './pricingFormat';

/**
 * Block 1b — the first real pricing signal on the page.
 *
 * Seven rows, weekday order (that is the mental model — sorting by any column would
 * break it). The decision column is `effectiveRevenue`: revenue earned per AVAILABLE
 * night. A night that sells half the time at 250 is worth less than one that sells every
 * time at 175, and without that single figure ops has to compare two columns in their head.
 *
 * ⚠️ It is NOT the product of the two columns beside it, and must never be labelled as
 * one. The achieved rate averages only the nights whose money is keyed in, while
 * occupancy counts every occupied night — so on a unit with un-keyed stays, multiplying
 * the two published figures overstates the result by exactly the un-priced share.
 *
 * Weekday names come from the numeric `dayOfWeek`, never from the API's `dayName` —
 * the API is language-neutral by design and the client owns the translation.
 */
// Stable reference: a fresh [] per render is the churn `EMPTY_BOOKINGS` exists to prevent
// in useBookings.js, and it is cheaper to not create the habit.
const EMPTY_ROWS = [];

const WeekdayStrip = ({ weekday }) => {
  const { t } = useLanguage();

  const rows = Array.isArray(weekday) ? weekday : EMPTY_ROWS;
  // Rank on the figure itself, not on the rate's sample. A weekday that NEVER sold earns
  // nothing per available night — the backend reports it as 0 — and it is usually the
  // genuinely weak day. Requiring a priced night to qualify excluded it and handed the
  // badge to a merely-cheaper weekday instead, inverting the point of the block.
  const rankable = rows.filter(d => typeof d.effectiveRevenue === 'number');

  // The weakest day, marked only when there is more than one to compare it against —
  // "the worst of one" is not a finding.
  const quietest = rankable.length > 1
    ? rankable.reduce((a, b) => (b.effectiveRevenue < a.effectiveRevenue ? b : a))
    : null;

  return (
    <section className="pg-block">
      <div className="pg-block__head">
        <h2 className="pg-block__title">
          <i className="bi bi-calendar-week" />
          {t('pricing.weekday.heading')}
        </h2>
        <p className="pg-block__hint">{t('pricing.weekday.hint')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="pg-table__empty">{t('pricing.weekday.empty')}</p>
      ) : (
        <table className="pg-table">
          <thead>
            <tr>
              <th>{t('pricing.weekday.day')}</th>
              <th>
                {t('pricing.weekday.achievedRate')}{' '}
                <span className="pg-footing">{t('pricing.common.exVat')}</span>
              </th>
              <th>{t('pricing.weekday.occupancy')}</th>
              <th title={t('pricing.weekday.effectiveRevenueHint')}>
                {t('pricing.weekday.effectiveRevenue')}{' '}
                <span className="pg-footing">{t('pricing.common.exVat')}</span>
              </th>
              <th>{t('pricing.weekday.sample')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(day => {
              const isQuiet = quietest && quietest.dayOfWeek === day.dayOfWeek;
              // The RATE's sample. Occupancy has its own denominator and is valid without
              // it — gating occupancy on this told ops "we know nothing about Fridays"
              // while the payload said Fridays were 92% full.
              const hasRateSample = day.sampleNights > 0;
              const hasOccupancy = typeof day.occupancyRate === 'number';

              return (
                <tr
                  key={day.dayOfWeek}
                  className={isQuiet ? 'pg-row--quiet' : undefined}
                >
                  <td>
                    {formatWeekdayName(day.dayOfWeek)}
                    {isQuiet && (
                      <span className="pg-tag pg-tag--quiet" style={{ marginInlineStart: 8 }}>
                        {t('pricing.weekday.quietest')}
                      </span>
                    )}
                  </td>
                  {/* A weekday that genuinely earned zero reports 0 — it is the weak day
                      this block exists to surface. Only a weekday with NO sample is blank. */}
                  <td data-figure>
                    {hasRateSample
                      ? `${money(day.achievedRate)} ${t('pricing.common.currency')}`
                      : <span className="pg-table__empty">{t('pricing.common.none')}</span>}
                  </td>
                  <td data-figure>
                    {hasOccupancy
                      ? percent(day.occupancyRate)
                      : <span className="pg-table__empty">{t('pricing.common.none')}</span>}
                  </td>
                  <td data-figure style={{ fontWeight: 700 }}>
                    {typeof day.effectiveRevenue === 'number'
                      ? `${money(day.effectiveRevenue)} ${t('pricing.common.currency')}`
                      : <span className="pg-table__empty">{t('pricing.common.none')}</span>}
                  </td>
                  <td data-figure>
                    {hasRateSample ? (
                      <>
                        {count(day.sampleNights)} {t('pricing.common.nights')}
                      </>
                    ) : (
                      // A weekday that WAS occupied but has no money keyed in is a
                      // different fact from one that never sold — say which.
                      <span className="pg-table__empty">
                        {day.occupiedNights > 0
                          ? t('pricing.weekday.noRateSample')
                          : t('pricing.weekday.noSample')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default WeekdayStrip;
